import { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import { useShopifySearch } from 'lib/hooks/useShopifySearch'
import { AnimatePresence, motion } from 'motion/react'
import { flattenConnection } from '@shopify/hydrogen-react'
import { useRouter } from 'next/router'

import { Button } from '../Button'
import { SearchIcon } from '../SearchIcon'
import { SearchResult } from './SearchResult'
import { ViewAllSearchLink } from './ViewAllSearchLink'

export const SearchButton = () => {
  const [open, setOpen] = useState(false)
  const widthRef = useRef<number>(60)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const router = useRouter()
  const { data, loading, query, reset, onChange } = useShopifySearch({
    limit: 3,
  })

  const isSearchOpen = useMemo(() => {
    return open ?? router.asPath.includes('/search')
  }, [open, router])

  const buttonRef = useCallback((node) => {
    if (node !== null) {
      widthRef.current = node.clientWidth
    }
  }, [])

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        setOpen(false)
      }
    }

    document.addEventListener('keydown', handleEsc)

    return () => {
      document.removeEventListener('keydown', handleEsc)
    }
  })

  const resetSearch = () => {
    setOpen(false)
    reset()
    if (inputRef.current) {
      inputRef.current.value = ''
    }
  }

  const focusInput = () => {
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }

  const closeIfFocusOutside = (e) => {
    if (
      containerRef.current &&
      !containerRef.current.contains(e.relatedTarget)
    ) {
      resetSearch()
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (query.length < 3) return
    router.push(`/search?q=${query}`, undefined, { scroll: false })
    resetSearch()
  }

  return (
    <div ref={containerRef} onBlur={closeIfFocusOutside}>
      <div className="relative">
        <motion.div
          initial={{ width: widthRef.current }}
          animate={{ width: isSearchOpen ? 300 : widthRef.current }}
          transition={{
            duration: 0.4,
            ease: [0.9, 0.01, 0.11, 0.98],
            delay: isSearchOpen ? 0 : 0.2,
          }}
          className="background-blur absolute right-0 top-0 z-[0] flex justify-end rounded-8 will-change-auto"
        >
          <motion.div
            initial={{ opacity: 1, pointerEvents: 'auto' }}
            animate={{
              opacity: isSearchOpen ? 0 : 1,
              pointerEvents: isSearchOpen ? 'none' : 'auto',
            }}
            transition={{
              duration: 0.3,
              ease: [0.9, 0.01, 0.11, 0.98],
              delay: isSearchOpen ? 0 : 0.2,
            }}
          >
            <Button
              ref={buttonRef}
              onClick={focusInput}
              onFocus={focusInput}
              tabIndex={isSearchOpen ? -1 : 0}
              className="leading-[16px]"
            >
              Search
            </Button>
          </motion.div>
        </motion.div>

        <motion.form
          className="relative z-[2] rounded-8"
          onSubmit={handleSubmit}
          initial={{ opacity: 0, pointerEvents: 'none' }}
          animate={{
            opacity: isSearchOpen ? 1 : 0,
            pointerEvents: isSearchOpen ? 'auto' : 'none',
          }}
          transition={{
            duration: 0.5,
            ease: [0.9, 0.01, 0.11, 0.98],
            delay: isSearchOpen ? 0.1 : 0,
          }}
        >
          <input
            type="text"
            ref={inputRef}
            onChange={onChange}
            onFocus={() => setOpen(true)}
            placeholder="what are you looking for?"
            className="h-full w-[300px] rounded-8 bg-transparent px-16 py-14 text-11 text-darkerGray placeholder-midGray focus:outline-none"
          />
          <button
            type="submit"
            className="absolute right-16 top-1/2 -translate-y-1/2"
          >
            <SearchIcon className="h-12 w-12" />
          </button>
        </motion.form>
      </div>

      <AnimatePresence>
        {(open && query.length > 2) || isSearchOpen ? (
          <motion.div
            initial={{ opacity: 0, y: 2 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{
              opacity: 0,
              y: -2,
              transition: {
                duration: 0.4,
                ease: [0.9, 0.01, 0.11, 0.98],
                delay: 0,
              },
            }}
            transition={{
              duration: 0.6,
              delay: 0.4,
              ease: [0.9, 0.01, 0.11, 0.98],
            }}
            className="background-blur absolute left-0 top-full mt-8 flex w-full flex-col gap-8 rounded-8 p-14"
          >
            {loading && data.length < 1 && <div>Searching for "{query}"</div>}
            {!loading && data.length < 1 && <div>No Results for "{query}"</div>}
            {data.length >= 1 &&
              data?.map((product: any) => {
                const images: any = flattenConnection(product.images)
                const src = images?.[0]?.src
                return (
                  <SearchResult
                    key={product._id}
                    {...product}
                    images={images}
                    src={src}
                    onClick={resetSearch}
                  />
                )
              })}
            {data.length >= 1 ? (
              <ViewAllSearchLink onClick={resetSearch} query={query} />
            ) : null}
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  )
}
